import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { de, tr } from 'date-fns/locale';
import { Download, Eye, Trash2, Loader2, FileText, CheckSquare, FileSpreadsheet, FileDown, MoreHorizontal, CheckCheck, Clock, CheckCircle, XCircle, Archive, Send, User, FileType } from 'lucide-react';
import { getSubmissions, getForms, exportSubmissions, deleteSubmission } from '@/lib/api';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatSafeDate = (value, formatStr = 'dd.MM.yyyy HH:mm', language = 'de') => {
  const d = safeDate(value);
  if (!d) return '-';
  const locale = language === 'tr' ? tr : de;
  return format(d, formatStr, { locale });
};

// Status badge component with i18n and business colors
const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  
  const configs = {
    submitted: { 
      labelKey: 'status.submitted', 
      className: 'bg-blue-100 text-blue-800 border-blue-200', 
      icon: Send 
    },
    processing: { 
      labelKey: 'status.processing', 
      className: 'bg-amber-100 text-amber-800 border-amber-200', 
      icon: Clock 
    },
    completed: { 
      labelKey: 'status.completed', 
      className: 'bg-emerald-100 text-emerald-800 border-emerald-200', 
      icon: CheckCircle 
    },
    approved: { 
      labelKey: 'status.approved', 
      className: 'bg-emerald-100 text-emerald-800 border-emerald-200', 
      icon: CheckCircle 
    },
    rejected: { 
      labelKey: 'status.rejected', 
      className: 'bg-red-100 text-red-800 border-red-200', 
      icon: XCircle 
    },
    archived: { 
      labelKey: 'status.archived', 
      className: 'bg-slate-100 text-slate-600 border-slate-200', 
      icon: Archive 
    },
  };
  
  const config = configs[status] || configs.submitted;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${config.className}`}>
      <Icon className="h-3 w-3" />
      {t(config.labelKey)}
    </span>
  );
};

export default function Submissions() {
  const { t, i18n } = useTranslation();
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bulkAction, setBulkAction] = useState(false);
  
  const [formStatusFilter, setFormStatusFilter] = useState('active');
  const [formSearch, setFormSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 50;

  useEffect(() => {
    loadForms();
  }, [formStatusFilter, formSearch]);

  useEffect(() => {
    if (selectedForm) {
      setPage(1);
      setSelectedIds([]);
      loadSubmissions();
    }
  }, [selectedForm, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (selectedForm) {
      loadSubmissions();
    }
  }, [page]);

  const loadForms = async () => {
    try {
      setLoading(true);
      const status = formStatusFilter === 'active' ? 'live' : formStatusFilter === 'inactive' ? 'archived' : null;
      const data = await getForms(status);
      const filtered = data.filter(f => !formSearch || f.name.toLowerCase().includes(formSearch.toLowerCase()));
      setForms(filtered);
      if (filtered.length > 0 && !selectedForm) {
        setSelectedForm(filtered[0]);
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    if (!selectedForm) return;
    try {
      setSubmissionsLoading(true);
      const response = await getSubmissions(selectedForm.id, page, perPage);
      const data = response.submissions || response.items || response;
      setSubmissions(Array.isArray(data) ? data : []);
      if (response.total) {
        setTotalPages(Math.ceil(response.total / perPage));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
      setSubmissions([]);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleExport = async (exportFormat = 'csv') => {
    if (!selectedForm) return;
    setExporting(true);
    try {
      await exportSubmissions(selectedForm.id, null, dateFrom, dateTo, exportFormat);
      toast.success(t('success.updated'));
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async (submissionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/submissions/${submissionId}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `submission_${submissionId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('success.updated'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('submissions.confirmDelete'))) return;
    try {
      await deleteSubmission(id);
      toast.success(t('success.deleted'));
      loadSubmissions();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredSubmissions.map(s => s.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedIds.length === 0) return;
    setBulkAction(true);
    try {
      await axios.post(`${API_URL}/api/submissions/bulk/status`, {
        submission_ids: selectedIds,
        status: newStatus
      }, { headers: getAuthHeader() });
      toast.success(t('success.updated'));
      setSelectedIds([]);
      loadSubmissions();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setBulkAction(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length} ${t('submissions.confirmBulkDelete')}`)) return;
    setBulkAction(true);
    try {
      await axios.post(`${API_URL}/api/submissions/bulk/delete`, {
        submission_ids: selectedIds
      }, { headers: getAuthHeader() });
      toast.success(t('success.deleted'));
      setSelectedIds([]);
      loadSubmissions();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setBulkAction(false);
    }
  };

  const handleBulkExport = async () => {
    if (selectedIds.length === 0) return;
    try {
      const response = await axios.post(`${API_URL}/api/submissions/bulk/export`, {
        submission_ids: selectedIds,
        format: 'csv'
      }, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulk_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('success.updated'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (statusFilter !== '__all__' && sub.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const searchableText = JSON.stringify(sub.data).toLowerCase();
      const senderName = (sub.sender_name || '').toLowerCase();
      const summary = (sub.summary || '').toLowerCase();
      if (!searchableText.includes(query) && !senderName.includes(query) && !summary.includes(query)) {
        return false;
      }
    }
    if (dateFrom) {
      const subDate = safeDate(sub.created_at);
      const fromDate = safeDate(dateFrom);
      if (subDate && fromDate && subDate < fromDate) return false;
    }
    if (dateTo) {
      const subDate = safeDate(sub.created_at);
      const toDate = safeDate(dateTo);
      if (subDate && toDate && subDate > toDate) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fa] p-6">
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('submissions.title')}</h1>
          <p className="text-gray-500 mt-1">{t('submissions.manageSubmissions')}</p>
        </div>

        <div className="grid grid-cols-4 gap-6">
          {/* Left: Form List */}
          <Card className="col-span-1 bg-white shadow-sm border border-slate-200/60">
            <CardHeader>
              <div className="space-y-3">
                <CardTitle className="text-base font-semibold text-slate-800">{t('submissions.forms')}</CardTitle>
                <Input 
                  placeholder={t('submissions.searchForm')} 
                  value={formSearch} 
                  onChange={(e) => setFormSearch(e.target.value)} 
                  className="h-9 bg-slate-50/50 border-slate-200" 
                />
                <Select value={formStatusFilter} onValueChange={setFormStatusFilter}>
                  <SelectTrigger className="h-9 bg-slate-50/50 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('submissions.active')}</SelectItem>
                    <SelectItem value="inactive">{t('submissions.inactive')}</SelectItem>
                    <SelectItem value="__all__">{t('submissions.allForms')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[700px] overflow-y-auto">
              {forms.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">{t('submissions.noForms')}</p>
              ) : (
                forms.map((form) => (
                  <button
                    key={form.id}
                    onClick={() => setSelectedForm(form)}
                    className={`w-full text-left px-3 py-3 rounded-lg transition-all ${
                      selectedForm?.id === form.id 
                        ? 'bg-blue-50 text-blue-700 font-medium shadow-sm' 
                        : 'hover:bg-slate-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{form.name}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {form.submission_count || 0} {t('submissions.submissionsCount')}
                        </p>
                      </div>
                      {form.status === 'live' && (
                        <CheckSquare className="h-4 w-4 text-green-500 mt-0.5 ml-2 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          {/* Right: Submissions */}
          <div className="col-span-3 space-y-4">
            {!selectedForm ? (
              <Card className="bg-white shadow-sm border border-slate-200/60">
                <CardContent className="py-20 text-center">
                  <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{t('submissions.selectForm')}</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card className="bg-white shadow-sm border border-slate-200/60">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-800">{selectedForm.name}</CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          {filteredSubmissions.length} {t('submissions.submissionsCount')}
                          {selectedIds.length > 0 && ` · ${selectedIds.length} ${t('submissions.selected')}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {selectedIds.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" disabled={bulkAction} className="shadow-sm">
                                {bulkAction ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCheck className="h-4 w-4 mr-2" />
                                )}
                                {t('submissions.bulkActions')} ({selectedIds.length})
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleBulkStatusChange('processing')}>
                                {t('submissions.statusProcessing')}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleBulkStatusChange('completed')}>
                                {t('submissions.statusCompleted')}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleBulkStatusChange('archived')}>
                                {t('submissions.statusArchived')}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={handleBulkExport}>
                                <Download className="h-4 w-4 mr-2" />
                                {t('submissions.exportSelection')}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={handleBulkDelete} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t('submissions.deleteSelection')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="outline" 
                              disabled={exporting || filteredSubmissions.length === 0}
                              className="shadow-sm"
                            >
                              {exporting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4 mr-2" />
                              )}
                              {t('common.export')}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleExport('csv')}>
                              <FileText className="h-4 w-4 mr-2" />
                              {t('submissions.csvExport')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                              <FileSpreadsheet className="h-4 w-4 mr-2" />
                              {t('submissions.xlsxExport')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="bg-white shadow-sm border border-slate-200/60">
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-4 gap-3">
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="bg-slate-50/50 border-slate-200">
                          <SelectValue placeholder={t('common.status')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">{t('submissions.allStatus')}</SelectItem>
                          <SelectItem value="submitted">{t('status.submitted')}</SelectItem>
                          <SelectItem value="processing">{t('status.processing')}</SelectItem>
                          <SelectItem value="completed">{t('status.completed')}</SelectItem>
                          <SelectItem value="archived">{t('status.archived')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input 
                        type="date" 
                        value={dateFrom} 
                        onChange={(e) => setDateFrom(e.target.value)}
                        placeholder={t('submissions.dateFrom')}
                        className="bg-slate-50/50 border-slate-200"
                      />
                      <Input 
                        type="date" 
                        value={dateTo} 
                        onChange={(e) => setDateTo(e.target.value)}
                        placeholder={t('submissions.dateTo')}
                        className="bg-slate-50/50 border-slate-200"
                      />
                      <Input 
                        placeholder={t('submissions.searchPlaceholder')} 
                        value={searchQuery} 
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-slate-50/50 border-slate-200"
                      />
                    </div>
                  </CardContent>
                </Card>

                {submissionsLoading ? (
                  <Card className="bg-white shadow-sm border border-slate-200/60">
                    <CardContent className="py-12 text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" />
                    </CardContent>
                  </Card>
                ) : filteredSubmissions.length === 0 ? (
                  <Card className="bg-white shadow-sm border border-slate-200/60">
                    <CardContent className="py-12 text-center">
                      <p className="text-gray-500">{t('submissions.noSubmissionsFound')}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-white shadow-sm border border-slate-200/60 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/80 border-b border-slate-200 hover:bg-slate-50/80">
                          <TableHead className="w-10">
                            <Checkbox
                              checked={selectedIds.length === filteredSubmissions.length && filteredSubmissions.length > 0}
                              onCheckedChange={handleSelectAll}
                            />
                          </TableHead>
                          <TableHead className="font-semibold text-slate-700">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              {t('submissions.sender')}
                            </div>
                          </TableHead>
                          <TableHead className="font-semibold text-slate-700">
                            <div className="flex items-center gap-2">
                              <FileType className="h-4 w-4" />
                              {t('submissions.summary')}
                            </div>
                          </TableHead>
                          <TableHead className="font-semibold text-slate-700">{t('common.status')}</TableHead>
                          <TableHead className="font-semibold text-slate-700">{t('submissions.submittedAt')}</TableHead>
                          <TableHead className="text-right font-semibold text-slate-700">{t('common.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSubmissions.map((submission) => (
                          <TableRow 
                            key={submission.id} 
                            className="hover:bg-slate-50/50 transition-colors border-b border-slate-100"
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedIds.includes(submission.id)}
                                onCheckedChange={(checked) => handleSelectOne(submission.id, checked)}
                              />
                            </TableCell>
                            <TableCell className="font-medium text-slate-700">
                              {submission.sender_name === null ? (
                                <span className="text-slate-500 italic text-sm">Anonym</span>
                              ) : submission.sender_name ? (
                                submission.sender_name
                              ) : (
                                <span className="text-slate-400 italic text-sm">
                                  {submission.id.slice(0, 8)}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-600 text-sm max-w-xs truncate">
                              {submission.summary || (
                                <span className="text-slate-400 italic">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={submission.status || 'submitted'} />
                            </TableCell>
                            <TableCell className="text-slate-600 text-sm">
                              {formatSafeDate(submission.created_at, 'dd.MM.yyyy HH:mm', i18n.language)}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem asChild>
                                    <Link to={`/admin/submissions/${submission.id}`} className="flex items-center">
                                      <Eye className="h-4 w-4 mr-2" />
                                      {t('submissions.viewSubmission')}
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleExportPdf(submission.id)}>
                                    <FileDown className="h-4 w-4 mr-2" />
                                    {t('submissions.pdfExport')}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleDelete(submission.id)} 
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t('common.delete')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                )}

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.max(1, p - 1))} 
                      disabled={page === 1}
                      className="shadow-sm"
                    >
                      {t('submissions.back')}
                    </Button>
                    <span className="text-sm text-slate-600">
                      {t('submissions.page')} {page} {t('submissions.of')} {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                      disabled={page === totalPages}
                      className="shadow-sm"
                    >
                      {t('submissions.next')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
